import { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperInstance } from 'swiper';

import 'swiper/css';
import 'swiper/css/pagination';

const tabs = [
  { label: 'Sports', value: 'sports', icon: 'icon-game-hall-sports' },
  { label: 'Live', value: 'live', icon: 'icon-game-hall-live' },
  { label: 'Table', value: 'table', icon: 'icon-game-hall-table' },
  { label: 'Slot', value: 'slot', icon: 'icon-game-hall-slot' },
  { label: 'Fishing', value: 'fishing', icon: 'icon-game-hall-fishing' },
  { label: 'Egame', value: 'virtual', icon: 'icon-game-hall-egame' },
];

const TAB_GAP = 12;

type CasinoTabsProps = {
  onChange: (value: string) => void;
};

export function CasinoTabs({ onChange }: CasinoTabsProps) {
  const [active, setActive] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [overlayStyle, setOverlayStyle] = useState({ width: 0, x: 0 });
  const swiperRef = useRef<SwiperInstance | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const currentIndex = hoveredIndex ?? active;
  const updateOverlay = useCallback(() => {
    const swiper = swiperRef.current;
    const tabElement = tabRefs.current[currentIndex];
    const slideElement = tabElement?.closest('.swiper-slide') as HTMLElement | null;

    if (!swiper || !tabElement || !slideElement) return;

    setOverlayStyle({
      width: tabElement.offsetWidth,
      x: slideElement.offsetLeft + swiper.translate,
    });
  }, [currentIndex]);

  useEffect(() => {
    updateOverlay();
  }, [updateOverlay]);

  useEffect(() => {
    window.addEventListener('resize', updateOverlay);

    return () => {
      window.removeEventListener('resize', updateOverlay);
    };
  }, [updateOverlay]);

  return (
    <div className="relative w-full overflow-hidden border-t border-[#2c2c2c] bg-[#1b1f23] animate-fade-in">
      <div
        className="pointer-events-none absolute left-0 top-1.5 z-[1] transition-[transform,width] duration-300"
        style={{
          width: overlayStyle.width,
          transform: `translate3d(${overlayStyle.x}px, 0, 0)`,
        }}
      >
        <div className="h-[5rem] w-full rounded-lg bg-white/10" />
      </div>

      <Swiper
        modules={[Pagination]}
        slidesPerView="auto"
        centeredSlides={false}
        spaceBetween={TAB_GAP}
        initialSlide={0}
        pagination={{
          clickable: true,
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          requestAnimationFrame(updateOverlay);
        }}
        onSetTranslate={(_, translate) => {
          const swiper = swiperRef.current;
          const tabElement = tabRefs.current[currentIndex];
          const slideElement = tabElement?.closest('.swiper-slide') as HTMLElement | null;

          if (!swiper || !tabElement || !slideElement) return;

          setOverlayStyle({
            width: tabElement.offsetWidth,
            x: slideElement.offsetLeft + translate,
          });
        }}
        className="casino-tabs pb-6"
      >
        {tabs.map((tab, index) => (
          <SwiperSlide key={tab.value} className="!w-[4.875rem]">
            <button
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              type="button"
              onClick={() => {
                setActive(index);
                onChange(tab.value);
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`group relative z-10 flex h-[5rem] w-full flex-col items-center justify-between rounded-lg transition-colors duration-300 ${
                active === index ? 'text-primary' : 'text-[#8a8a8a]'
              }`}
            >
              <span className="relative z-10 pt-2 text-sm font-medium transition-colors duration-200 group-hover:text-primary">
                {tab.label}
              </span>

              <i
                className={`${tab.icon} relative z-10 mb-1 text-[3.25rem] transition-colors duration-200 group-hover:text-primary`}
              />
            </button>
          </SwiperSlide>
        ))}
      </Swiper>

      <style>
        {`
          .casino-tabs .swiper-pagination {
            bottom: 0 !important;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 0.625rem;
            padding-bottom: 0.5rem;
          }

          .casino-tabs .swiper-pagination-bullet {
            width: 0.45rem;
            height: 0.45rem;
            margin: 0 !important;
            background: white;
            opacity: 1;
            border-radius: 999px;
          }

          .casino-tabs .swiper-pagination-bullet-active {
            width: 1.5rem;
            height: 0.3rem;
            background: #ffd400;
            border-radius: 999px;
            opacity: 1;
          }
        `}
      </style>
    </div>
  );
}
